import { observer } from 'mobx-react-lite'
import { useCallback, type ChangeEvent, useEffect, useRef } from 'react'

import { Search, PageHeader } from '@/shared'
import { doctorsState, doctorState } from '@App/store'
import { type IUpdateFeedback } from '@App/types/doctorsType'
import { onUseSearchDebounce } from '@App/utils'
import { defaultDoctorsFilters } from '@Config/config'

import style from './Doctors.module.scss'
import { Filter } from './Filter/Filter'
import { List } from './List/List'
import { UnpublishedFeedbacks } from './UnpublishedFeedbacks/UnpublishedFeedbacks'

export const Doctors = observer(() => {
    const ref = useRef<HTMLInputElement | null>(null)

    const onGetFilteredList = useCallback((filterKey: string, opt: string | null) => {
        doctorsState.onSetFilters({ ...defaultDoctorsFilters, [filterKey]: opt })
        doctorsState.onSetDoctorsList(null)
        doctorsState.onGetDoctorsList()
    }, [])

    const onGetDoctorsListCallBack = useCallback(() => {
        if (!doctorsState.list) {
            doctorsState.onGetDoctorsList()
        }
        return () => {
            doctorsState.onSetDoctorsList(null)
            doctorsState.onSetTotalCount(0)
            doctorsState.onSetFilters(defaultDoctorsFilters)
            doctorsState.onSetUnpublishedFeedbacks(null)
        }
    }, [])

    const onGetUnpublishedFeedbacks = useCallback(() => {
        if (!doctorsState.unpublishedFeedbacks) {
            doctorsState.onGetUnpublishedFeedbacks()
        }
    }, [])

    const onGetAdditionalDoctors = useCallback(() => {
        if (!doctorsState.isFetchingData) {
            const { limit, offset } = doctorsState.filters
            const isAllList = limit * offset >= doctorsState.totalCount
            !isAllList && doctorsState.onGetDoctorsList({ isAdditional: true })
        }
    }, [])

    const onDebouncesSearch = onUseSearchDebounce((search) => {
        doctorsState.onSetFilters({
            ...doctorsState.filters,
            search,
            limit: defaultDoctorsFilters.limit,
            offset: defaultDoctorsFilters.offset,
            post: null,
            photo: 'all'
        })
        doctorsState.onGetDoctorsList()
    }, 500)

    const onSearch = (e: ChangeEvent<HTMLInputElement>) => {
        onDebouncesSearch(e.target.value)
    }

    useEffect(onGetDoctorsListCallBack, [onGetDoctorsListCallBack])
    useEffect(onGetUnpublishedFeedbacks, [onGetUnpublishedFeedbacks])

    const onGetPageItemsQuantity = (quantity: number) => {
        const cases = [2, 0, 1, 1, 1, 2]
        const titles = ['специалист', 'специалиста', 'специалистов']

        if (quantity % 100 > 4 && quantity % 100 < 20) {
            return `${quantity} ${titles[2]}`
        } else {
            return `${quantity} ${titles[cases[Math.min(quantity % 10, 5)]]}`
        }
    }

    const onSuccessRemoveFeedbackFromList = (feedbackId: string) => {
        if (doctorsState.unpublishedFeedbacks) {
            doctorsState.onSetUnpublishedFeedbacks(
                doctorsState.unpublishedFeedbacks.filter((feedback) => feedback.id !== feedbackId)
            )
        }
    }

    const onRemoveFeedbackHandler = (id: string, feedbackId: string, isPublished: boolean) => {
        doctorState.onRemoveFeedback(id, feedbackId, isPublished, () => onSuccessRemoveFeedbackFromList(feedbackId))
    }

    const onSetFeedbackStartDatefilter = (startDate: string | null) => {
        doctorsState.onSetFeedbacksDatesFilter({ ...doctorsState.feedbacksDatesFilter, startDate })
    }

    const onSetFeedbackEndDatefilter = (endDate: string | null) => {
        doctorsState.onSetFeedbacksDatesFilter({ ...doctorsState.feedbacksDatesFilter, endDate })
    }

    const onPublishFeedbackHandler = (dto: IUpdateFeedback) => doctorsState.onPublishFeedback(dto)

    return (
        <div className={style.container}>
            <PageHeader pageName={'Врачи'} pageInfo={onGetPageItemsQuantity(doctorsState.totalCount)} />
            <div className={style.searchWrapper}>
                <Search ref={ref} onSearch={onSearch} placeholder='Поиск по врачам' />
            </div>
            <Filter filters={doctorsState.filters} onGetFilteredList={onGetFilteredList} />
            {doctorsState.unpublishedFeedbacks && doctorsState.unpublishedFeedbacks.length > 0 && (
                <UnpublishedFeedbacks
                    unpublishedFeedbacks={doctorsState.unpublishedFeedbacks}
                    feedbacksDatesFilter={doctorsState.feedbacksDatesFilter}
                    isFetchingFeedbacks={doctorState.isFetchingFeedbacks}
                    onGetUnpublishedFeedbacks={doctorsState.onGetUnpublishedFeedbacks}
                    onRemoveFeedback={onRemoveFeedbackHandler}
                    onPublishFeedback={onPublishFeedbackHandler}
                    onSetFeedbackStartDatefilter={onSetFeedbackStartDatefilter}
                    onSetFeedbackEndDatefilter={onSetFeedbackEndDatefilter}
                />
            )}
            <List
                list={doctorsState.list}
                getDoctorsCallback={onGetAdditionalDoctors}
                isFetching={doctorsState.isFetchingData}
            />
        </div>
    )
})
